import { useState, useEffect, useMemo } from 'react';
import { X, Check, DollarSign, Percent, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { blockNonNumericPaste } from './numericInputUtils';
import ModalCloseButton from './ModalCloseButton';

const CASH_KEY = '__CASH__';

/**
 * ContributionAllocationModal — lets the user split their monthly
 * contribution across the tickers they own (plus a cash bucket).
 *
 * Two modes:
 *   • Dollars  — values are absolute $/month per ticker; over-allocating
 *               grays out Done. Under-allocating routes the diff to cash
 *               with a toast warning when Done is clicked.
 *   • Percent  — values are 0-100 per ticker; anything not allocated
 *               routes to cash with a toast warning when Done is clicked.
 *               Cannot exceed 100% total.
 *
 * Closing without saving (X / overlay click) reverts staged changes.
 * Done commits the staged state up to the parent via onSave(...).
 *
 * "No allocation set" = the parent stores an empty `allocations` dict;
 * the calc loop in PortfolioDripModal then falls back to an even-split
 * with leftover pennies routed to the highest-yield ticker.
 */
export default function ContributionAllocationModal({
  open,
  onClose,
  onSave,
  monthlyContrib,
  initialMode,
  initialAllocations,
  stocks,
}) {
  // Local staged state — only commits to parent on Done.
  const [mode, setMode] = useState(initialMode || 'dollars');
  const [values, setValues] = useState({});
  const [budget, setBudget] = useState(monthlyContrib || 0);

  // Hydrate when the modal opens (or its inputs change while open).
  useEffect(() => {
    if (!open) return;
    setMode(initialMode || 'dollars');
    setValues({ ...(initialAllocations || {}) });
    setBudget(Number(monthlyContrib) || 0);
  }, [open, initialMode, initialAllocations, monthlyContrib]);

  const totalAlloc = useMemo(() => {
    return Object.values(values).reduce((acc, v) => acc + (Number(v) || 0), 0);
  }, [values]);

  // Cap depends on mode. In dollars mode the cap is the budget; in
  // percent mode it's 100. Numbers above the cap gray out Done.
  const cap = mode === 'percent' ? 100 : Number(budget) || 0;
  const overAllocated = totalAlloc > cap + 0.0001;
  const remaining = Math.max(0, cap - totalAlloc);
  const hasAnyValue = Object.values(values).some((v) => Number(v) > 0);

  const updateValue = (key, raw) => {
    const n = Math.max(0, Number(raw) || 0);
    setValues((prev) => ({ ...prev, [key]: n }));
  };

  const clearRow = (key) => {
    setValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const evenSplit = () => {
    if (!stocks || stocks.length === 0) return;
    if (mode === 'percent') {
      const each = Number((100 / stocks.length).toFixed(2));
      const next = {};
      stocks.forEach((s, i) => {
        // Round to 2 decimals; assign any leftover to the first row.
        next[s.ticker] = i === 0
          ? Number((100 - each * (stocks.length - 1)).toFixed(2))
          : each;
      });
      setValues(next);
    } else {
      // Dollars: leftover pennies go to highest-yield ticker.
      const cents = Math.floor((Number(budget) * 100) / stocks.length);
      const totalCents = Math.round(Number(budget) * 100);
      const leftover = totalCents - cents * stocks.length;
      let hiYieldIdx = 0;
      let hiY = -Infinity;
      stocks.forEach((s, i) => {
        const y = Number(s.yield) || 0;
        if (y > hiY) {
          hiY = y;
          hiYieldIdx = i;
        }
      });
      const next = {};
      stocks.forEach((s, i) => {
        next[s.ticker] = (cents + (i === hiYieldIdx ? leftover : 0)) / 100;
      });
      setValues(next);
    }
    toast.success('Even split applied', {
      description: 'Leftover pennies route to the highest-yield ticker.',
      duration: 2200,
    });
  };

  const handleDone = () => {
    if (overAllocated) return;
    // Toast warnings based on under-allocation.
    if (mode === 'dollars' && hasAnyValue && totalAlloc < cap - 0.0001) {
      toast.warning(
        `$${(cap - totalAlloc).toFixed(2)} unallocated → cash bucket`,
        {
          description: 'Anything you don\'t assign to a ticker accumulates as cash.',
          duration: 4500,
        },
      );
    }
    if (mode === 'percent' && hasAnyValue && totalAlloc < 100 - 0.0001) {
      toast.warning(
        `${(100 - totalAlloc).toFixed(2)}% unallocated → cash bucket`,
        {
          description: 'Anything you don\'t assign to a ticker accumulates as cash.',
          duration: 4500,
        },
      );
    }
    // Strip empty / zero values so the parent stores a clean dict
    // (an empty dict tells the calc loop to use the even-split fallback).
    const cleaned = {};
    Object.entries(values).forEach(([k, v]) => {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) cleaned[k] = n;
    });
    onSave({ mode, allocations: cleaned, monthly_contrib: Number(budget) || 0 });
    onClose();
  };

  if (!open) return null;

  const fmtVal = (v) => {
    if (v == null || v === '') return '';
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : '';
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-4"
      onClick={onClose}
      data-testid="alloc-modal-overlay"
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="alloc-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-base font-bold">Allocate Monthly Contribution</h3>
            <p className="text-xs text-muted-foreground">
              Split your monthly buy across your portfolio (or hold cash).
            </p>
          </div>
          <ModalCloseButton onClick={onClose} testId="alloc-modal-close" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24 sm:pb-4">
          {/* Total budget */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">
              Monthly Contribution ($)
            </label>
            <input
              type="number"
              min="0"
              step="50"
              value={budget}
              onChange={(e) => setBudget(Math.max(0, Number(e.target.value) || 0))}
              onPaste={blockNonNumericPaste}
              className="w-full h-10 rounded-lg border bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="alloc-budget-input"
            />
          </div>

          {/* Mode toggle — pill radios */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1">Allocate by</p>
            <div className="flex items-center gap-2 rounded-full border p-1 bg-muted/40 w-fit">
              <button
                type="button"
                onClick={() => setMode('dollars')}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  mode === 'dollars'
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="alloc-mode-dollars"
              >
                <DollarSign className="w-3.5 h-3.5" />
                Dollars
              </button>
              <button
                type="button"
                onClick={() => setMode('percent')}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  mode === 'percent'
                    ? 'bg-violet-500 text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="alloc-mode-percent"
              >
                <Percent className="w-3.5 h-3.5" />
                Percent
              </button>
            </div>
          </div>

          {/* Helper button */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={evenSplit}
              className="text-[11px] font-semibold px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              data-testid="alloc-even-split-btn"
            >
              ⚡ Even split
            </button>
            <button
              type="button"
              onClick={() => setValues({})}
              className="text-[11px] font-semibold px-2 py-1 rounded-md bg-muted hover:bg-muted/70 text-muted-foreground transition-colors"
              data-testid="alloc-clear-btn"
            >
              Clear all
            </button>
          </div>

          {/* Allocation rows */}
          <div className="space-y-1.5">
            {(stocks || []).map((s) => (
              <div
                key={s.ticker}
                className="flex items-center gap-2 p-2 rounded-lg border bg-card"
                data-testid={`alloc-row-${s.ticker}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-sm font-bold">{s.ticker}</span>
                    {Number.isFinite(s.yield) && s.yield > 0 && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono">
                        {s.yield.toFixed(2)}%
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {s.name || ''}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step={mode === 'percent' ? '1' : '5'}
                    value={fmtVal(values[s.ticker])}
                    onChange={(e) => updateValue(s.ticker, e.target.value)}
                    onPaste={blockNonNumericPaste}
                    placeholder="0"
                    className="w-20 h-8 rounded-md border bg-background pl-2 pr-6 font-mono text-xs text-right focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid={`alloc-input-${s.ticker}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                    {mode === 'percent' ? '%' : '$'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => clearRow(s.ticker)}
                  className="text-muted-foreground hover:text-rose-500 transition-colors"
                  data-testid={`alloc-clear-row-${s.ticker}`}
                  title="Clear this row"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {/* Just Cash row — same shape as a ticker row, special key. */}
            <div
              className="flex items-center gap-2 p-2 rounded-lg border-2 border-dashed border-amber-300/60 dark:border-amber-700/60 bg-amber-50/40 dark:bg-amber-950/20"
              data-testid="alloc-row-cash"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Banknote className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                  <span className="font-mono text-sm font-bold">Just Cash</span>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Sit out — money accrues, earns nothing, no dividend.
                </p>
              </div>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step={mode === 'percent' ? '1' : '5'}
                  value={fmtVal(values[CASH_KEY])}
                  onChange={(e) => updateValue(CASH_KEY, e.target.value)}
                  onPaste={blockNonNumericPaste}
                  placeholder="0"
                  className="w-20 h-8 rounded-md border bg-background pl-2 pr-6 font-mono text-xs text-right focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="alloc-input-cash"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                  {mode === 'percent' ? '%' : '$'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => clearRow(CASH_KEY)}
                className="text-muted-foreground hover:text-rose-500 transition-colors"
                data-testid="alloc-clear-row-cash"
                title="Clear cash allocation"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Totals indicator */}
          <div
            className={`flex items-center justify-between text-xs font-mono px-2 py-1.5 rounded-md border ${
              overAllocated
                ? 'bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300'
                : remaining > 0.01
                  ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-300/60 dark:border-amber-800/60 text-amber-700 dark:text-amber-300'
                  : 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300/60 dark:border-emerald-800/60 text-emerald-700 dark:text-emerald-300'
            }`}
            data-testid="alloc-totals"
          >
            <span>
              Allocated: {mode === 'percent' ? `${totalAlloc.toFixed(2)}%` : `$${totalAlloc.toFixed(2)}`}
            </span>
            {overAllocated ? (
              <span className="font-bold">Over by {mode === 'percent' ? `${(totalAlloc - cap).toFixed(2)}%` : `$${(totalAlloc - cap).toFixed(2)}`}</span>
            ) : remaining > 0.01 ? (
              <span>{mode === 'percent' ? `${remaining.toFixed(2)}%` : `$${remaining.toFixed(2)}`} → cash</span>
            ) : (
              <span className="font-bold">100% allocated</span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-3 border-t bg-muted/30">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border bg-background hover:bg-muted text-xs font-semibold transition-colors"
            data-testid="alloc-cancel-btn"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDone}
            disabled={overAllocated}
            className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="alloc-done-btn"
          >
            <Check className="w-3.5 h-3.5" />
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
